import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, switchMap, take, throwError } from 'rxjs';

/**
 * Interceptor que reage a respostas 401.
 *
 * Só força um novo login (`authorize()`) quando o usuário realmente NÃO está
 * autenticado. Um 401 transitório enquanto a sessão ainda é válida — típico de
 * requisições canceladas/lentas durante a carga inicial — NÃO deve disparar um
 * redirect: era exatamente isso que causava o loop /logged <-> challenge.
 *
 * Além disso, garante no máximo um `authorize()` por carga de página, evitando a
 * tempestade de redirects quando várias requisições recebem 401 ao mesmo tempo.
 * O flag é reinicializado naturalmente no reload após o redirect (e quando a
 * sessão volta a estar autenticada).
 */
let isRedirectingToLogin = false;

/** Uso exclusivo de testes: reinicia o guard entre casos. */
export function resetAuthRedirectGuardForTests(): void {
    isRedirectingToLogin = false;
}

export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
    const oidcSecurityService = inject(OidcSecurityService);

    return next(req).pipe(
        catchError((error: unknown) => {
            if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
                return throwError(() => error);
            }

            return oidcSecurityService.isAuthenticated$.pipe(
                take(1),
                switchMap(({ isAuthenticated }) => {
                    if (isAuthenticated) {
                        // Sessão ainda válida: 401 transitório. Rearma o guard e
                        // propaga o erro sem redirecionar.
                        isRedirectingToLogin = false;
                    } else if (!isRedirectingToLogin) {
                        isRedirectingToLogin = true;
                        oidcSecurityService.authorize();
                    }
                    return throwError(() => error);
                }),
            );
        }),
    );
};
